import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useRealtimeStatus } from '@/hooks/useRealtime';
import type { ConnectionStatus } from '@/types';

const LABELS: Record<ConnectionStatus, { text: string; tone: 'success' | 'warning' | 'error' | 'neutral' }> = {
  connected: { text: 'In tempo reale', tone: 'success' },
  connecting: { text: 'Connessione…', tone: 'warning' },
  disconnected: { text: 'Non connesso', tone: 'neutral' },
  error: { text: 'Errore di connessione', tone: 'error' },
};

const DESCRIPTIONS: Record<ConnectionStatus, string> = {
  connected: 'Gli aggiornamenti di servizi e ticket arrivano in tempo reale.',
  connecting: 'Connessione al canale di aggiornamento in corso.',
  disconnected: 'Aggiornamenti in tempo reale non attivi: i dati vengono ricaricati periodicamente.',
  error: 'Impossibile stabilire il canale in tempo reale. I dati vengono ricaricati periodicamente.',
};

export interface RealtimeIndicatorProps {
  /** Dot only, for the collapsed sidebar. */
  compact?: boolean;
}

/**
 * WebSocket connection state.
 *
 * Worth surfacing because it changes what the user can trust: when the socket
 * is down the dashboard is only as fresh as the last poll, and silently showing
 * stale service states would be worse than admitting it.
 */
export function RealtimeIndicator({ compact = false }: RealtimeIndicatorProps): JSX.Element {
  const status = useRealtimeStatus();
  const { text, tone } = LABELS[status];

  return (
    <Tooltip title={DESCRIPTIONS[status]}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', minWidth: 0 }}
        role="status"
        aria-label={`Stato aggiornamenti: ${text}`}
      >
        <Box
          aria-hidden="true"
          sx={(theme) => ({
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: theme.tones[tone].main,
            ...(status === 'connecting' && {
              animation: 'realtime-blink 1.2s ease-out infinite',
              '@keyframes realtime-blink': {
                '0%': { opacity: 1 },
                '12%': { opacity: 0.35 },
                '100%': { opacity: 1 },
              },
            }),
          })}
        />
        {!compact && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {text}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}
